import Coms from '@/communications'
import bus from '@/plugins/bus'
import isEmpty from 'lodash/isEmpty'
import delay from 'lodash/delay'
import isNil from 'lodash/isNil'
import axios from '@/plugins/axios'

export default {
  async uploadProfilePhoto({ commit, state }, { file, notify }) {
    const willNotify = isNil(notify) ? true : notify

    try {
      const token = await Coms.user.getUploadUserPhotoToken(state.id)

      const formData = new FormData()
      formData.append('image', file)

      const response = await axios.post('/api/v1/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Token': token,
          'Socket-Id': Coms.util.getSocketId()
        }
      })

      if (isEmpty(response.data.photo)) {
        const error = 'No image path was returned from the server.'
        if (willNotify) {
          bus.emit('notification', error, {
            color: 'error',
            dark: true
          })
        }
        throw new Error(error)
      }

      const photoPath = response.data.photo

      // Retry logic to ensure the photo is actually available
      const checkForAsset = async (url, maxTries = 10, delayMs = 1000) => {
        for (let tries = 0; tries < maxTries; tries++) {
          try {
            await axios.head(url)
            return true
          } catch (err) {
            await new Promise(res => setTimeout(res, delayMs))
          }
        }
        return false
      }

      const assetAvailable = await checkForAsset(photoPath)

      if (!assetAvailable) {
        const error = 'The image was uploaded, but the path obtained seems to be invalid.'
        if (willNotify) {
          bus.emit('notification', error, {
            color: 'error',
            dark: true
          })
        }
        throw new Error(error)
      }

      commit('setUserPhoto', photoPath)

      if (willNotify) {
        bus.emit('notification', 'The profile image was updated successfully.', {
          color: 'success',
          dark: true
        })
      }

      return photoPath

    } catch (err) {
      console.error(err)

      const message =
        err?.response?.data ||
        err?.message ||
        'Upload failed due to an unknown error.'

      if (willNotify) {
        bus.emit('notification', message, {
          color: 'error',
          dark: true
        })
      }

      throw err
    }
  },
  async deleteProfilePhoto({ commit, state }) {
    try {
      const data = await Coms.user.deleteUserPhoto(state.id)

      commit('setUserPhoto', null)
      bus.emit('notification', 'The profile image was deleted.', {
        color: 'success',
        dark: true
      })

      return data
    } catch (err) {
      console.error(err)
      bus.emit('notification', err, {
        color: 'error',
        dark: true
      })
      throw err
    }
  },
  async getUserData({ commit, state }, { id, notify } = {}) {
    const willNotify = isNil(notify) ? true : notify;
    try {
      // We pass an empty object if no id is provided, so the backend can default to the logged-in user.
      const data = await Coms.user.getUserData({ id });
      commit('setUserData', data);
      return data;
    } catch (err) {
      if (willNotify) {
        bus.emit('notification', err.message || 'Failed to get user data.', {
          color: 'error',
          dark: true
        });
      }
      console.error('getUserData action failed:', err);
      throw err;
    }
  },
  async getUserAbilities ({ commit, state }, notify) {
      const willNotify = isNil(notify) ? true : notify

  try {
    const data = await Coms.user.getUserAbilities(state.id)
    commit('setUserAbilities', data)
    return data
  } catch (err) {
    if (willNotify) {
      bus.emit('notification', 'Unable to fetch user account permission data due to an error.', {
        color: 'error',
        dark: true
      })
    }
    console.error(err)
    throw err
  }
  },
  // This needs to be called here instead of customer because we call it before
  // customer is initialized
  async checkMaintenanceMode({ commit, state }) {
    try {
      const data = await Coms.customer.checkMaintenanceByCustomer(state.customer_id)
      commit('customer/setMaintenanceMode', null, { root: true }, data)
      return data
    } catch (err) {
      bus.emit('notification', 'Unable to fetch user data due to an error.', {
        color: 'error',
        dark: true
      })
      console.error(err)
      throw err
    }
  }
}
